import type { BarSeriesOption, LineSeriesOption } from 'echarts/charts'
import type { DataZoomComponentOption, GridComponentOption, TooltipComponentOption } from 'echarts/components'
import type { ComposeOption } from 'echarts/core'
import type { SetRequiredDeep } from 'type-fest'
import * as zod from 'zod'
import type { DepartmentPageLine, DeviceBuffer } from '../Device'
import type { DateFormat } from '../PersonalSettings'
import { CARD_CHART, COMMON_WIDGET_DATA_CONSTRUCTOR, COMMON_WIDGET_ELEMENT_DATA, defaultInterval, IntervalLockSupportSchema, IntervalSupportSchema, WidgetElementSchema, WidgetSchema, WidgetSerieTypeSchema, type AbstractWidgetData, type BaseWidgetElementDialogProps, type GraphWidgetElementData, type IntervalSupport, type PartialWidget, type PartialWidgetElement, type WidgetBufferValue, type WidgetConstructor, type WidgetSerieType } from './Common'
import type { BaseWidgetDataRequest, WidgetDataResponse } from './Widgets'

export type CardChartOptions = ComposeOption<
  LineSeriesOption
  | BarSeriesOption
  | TooltipComponentOption
  | GridComponentOption
  | DataZoomComponentOption
>

export type CardChartDataDifference = {
  unit: string
  color: string
  value: string
  icon: string
  alignment: 'align-baseline' | 'fill-height justify-center' | 'fill-height justify-center align-baseline'
}
export type CardChartElementData = {
  unit: string
  name: string
  value: number
}
export type CardChartData = {
  current?: CardChartElementData
  compareTo?: CardChartElementData
  difference?: CardChartDataDifference
  showSparkline: boolean
  compareWith: CompareWith
}
export type CardChartWidgetDataRequest = BaseWidgetDataRequest & {
  type: 'CARD_CHART'
  widget: CardChartWidget
  dateFormat: DateFormat
}
export type CardChartWidgetOptionsResponse = WidgetDataResponse & {
  type: 'CARD_CHART'
  options: CardChartOptions
  data: CardChartData
}

const CompareWithSchema = zod.union([
  zod.literal('AVERAGE_VALUE'),
  zod.literal('MIN_VALUE'),
  zod.literal('MAX_VALUE'),
  zod.literal('ANOTHER_BUFFER'),
  zod.literal('CUSTOM_VALUE'),
  zod.literal('NONE'),
])
export type CompareWith = zod.infer<typeof CompareWithSchema>

const CompareTypeSchema = zod.union([
  zod.literal('NONE'),
  zod.literal('NUMERIC'),
  zod.literal('PERCENTAGE'),
])
export type CompareType = zod.infer<typeof CompareTypeSchema>

export const CardChartWidgetElementCompareToBaseSchema = WidgetElementSchema.extend({
  type: CARD_CHART,
  color: zod.string().nullable(),
  serieType: WidgetSerieTypeSchema,
})
const CardChartWidgetElementBaseSchema = CardChartWidgetElementCompareToBaseSchema.extend({
  showAxis: zod.boolean(),
  showSparkline: zod.boolean(),
  compareType: CompareTypeSchema,
  compareWith: CompareWithSchema,
})
const CardChartWidgetElementWithCompareToValue = CardChartWidgetElementBaseSchema.extend({
  compareWith: zod.literal('CUSTOM_VALUE'),
  compareToValue: zod.number(),
})
const CardChartWidgetElementWithCompareToAnotherBuffer = CardChartWidgetElementBaseSchema.extend({
  compareWith: zod.literal('ANOTHER_BUFFER'),
  compareTo: CardChartWidgetElementCompareToBaseSchema,
})
const CardChartWidgetElementWithCompareToAverageValue = CardChartWidgetElementBaseSchema.extend({
  compareWith: zod.literal('AVERAGE_VALUE'),
})
const CardChartWidgetElementWithCompareToMinValue = CardChartWidgetElementBaseSchema.extend({
  compareWith: zod.literal('MIN_VALUE'),
})
const CardChartWidgetElementWithCompareToMaxValue = CardChartWidgetElementBaseSchema.extend({
  compareWith: zod.literal('MAX_VALUE'),
})
const CardChartWidgetElementWithCompareToNone = CardChartWidgetElementBaseSchema.extend({
  compareWith: zod.literal('NONE'),
})

export const CardChartWidgetElementUnionSchema = zod.discriminatedUnion('compareWith', [
  CardChartWidgetElementWithCompareToValue,
  CardChartWidgetElementWithCompareToAnotherBuffer,
  CardChartWidgetElementWithCompareToAverageValue,
  CardChartWidgetElementWithCompareToMinValue,
  CardChartWidgetElementWithCompareToMaxValue,
  CardChartWidgetElementWithCompareToNone,
])
export type CardChartWidgetElementUnion = zod.infer<typeof CardChartWidgetElementUnionSchema>
export type CardChartWidgetElementUnionPartial = SetRequiredDeep<PartialWidgetElement<CardChartWidgetElementUnion>, 'type' | 'compareWith'>

export const CardChartWidgetElementSchema = CardChartWidgetElementCompareToBaseSchema.extend({
  type: CARD_CHART,
  showAxis: zod.boolean().nullish(),
  showSparkline: zod.boolean().nullish(),
  compareType: CompareTypeSchema.nullish(),
  compareWith: CompareWithSchema.nullish(),
  compareToValue: zod.number().nullish(),
  compareTo: CardChartWidgetElementCompareToBaseSchema.nullish(),
})
export type CardChartWidgetElementBase = zod.infer<typeof CardChartWidgetElementCompareToBaseSchema>
export type CardChartWidgetElement = zod.infer<typeof CardChartWidgetElementSchema>
export type CardChartWidgetElementPartial = PartialWidgetElement<CardChartWidgetElementBase>

export const CardChartWidgetSchema = WidgetSchema.merge(IntervalSupportSchema).merge(IntervalLockSupportSchema).extend({
  type: CARD_CHART,
  elements: CardChartWidgetElementUnionSchema.array().max(1),
})
export type CardChartWidget = zod.infer<typeof CardChartWidgetSchema>
export type CardChartWidgetPartial = SetRequiredDeep<PartialWidget<CardChartWidget>, 'type' | 'elements' | `elements.${number}.type` | `elements.${number}.compareWith`>

export interface CardChartWidgetElementData extends GraphWidgetElementData {
  showAxis: boolean
  showSparkline: boolean
  compareWith: CompareWith
  compareType: CompareType
  serieType: WidgetSerieType
  compareToValue: number | null
  compareTo: CardChartWidgetElementData | null
  values: WidgetBufferValue[]
  availableUnits: Record<string, string>
}
export interface CardChartWidgetData extends AbstractWidgetData<CardChartWidgetElementData>, IntervalSupport {
  type: 'CARD_CHART'
  elements: CardChartWidgetElementData[]
  timestamps: string[]
}
export interface CardChartWidgetElementDialogProps extends BaseWidgetElementDialogProps {
  type: 'CARD_CHART'
  line: DepartmentPageLine
  buffer?: DeviceBuffer
  enabledAdvancedSettings?: boolean
  enableCompareWith?: boolean
  enableAxisSettings?: boolean
  enableColorPicker?: boolean
  enableSparkline?: boolean
  validationSchema?: zod.ZodSchema
}

export const DEFAULT_CARD_CHART_WIDGET_ELEMENT: CardChartWidgetElementUnionPartial = {
  ...COMMON_WIDGET_ELEMENT_DATA,
  type: 'CARD_CHART',
  compareType: 'NUMERIC',
  compareWith: 'NONE',
  color: null,
  serieType: 'line',
  showAxis: false,
  showSparkline: false,
}
export const CARD_CHART_WIDGET_CONSTRUCTOR: WidgetConstructor<CardChartWidgetPartial> = () => ({
  type: 'CARD_CHART',
  ...COMMON_WIDGET_DATA_CONSTRUCTOR(),
  interval: defaultInterval(),
  elements: [DEFAULT_CARD_CHART_WIDGET_ELEMENT],
})
